/**
 * Landing Content Store — the homepage copy, shared so it can be edited in
 * place the same way the interface strings are.
 *
 * The homepage used to fetch this into its own state. Holding it in a store
 * instead means the text editor can write a change and the page behind the
 * drawer shows it straight away, without a reload.
 *
 * Keys are the ones the API uses: `landing_hero_headline` for English and
 * `landing_hero_headline_de` for German.
 */

import { create } from 'zustand';
import { api } from '@/lib/api';

interface LandingContentState {
  content: Record<string, string>;
  loaded: boolean;
  loading: boolean;
  load: () => Promise<void>;
  save: (key: string, en: string, de: string) => Promise<void>;
}

/** Landing keys are the only ones with this prefix; locale keys use dots. */
export function isLandingKey(key: string): boolean {
  return key.startsWith('landing_');
}

/** The value for a key in the reader's language, German falling back to English. */
export function landingValue(content: Record<string, string>, key: string, language: string): string {
  if (language === 'de') {
    const german = content[`${key}_de`];
    if (german) return german;
  }
  return content[key] || '';
}

export const useLandingContentStore = create<LandingContentState>((set, get) => ({
  content: {},
  loaded: false,
  loading: false,

  load: async () => {
    if (get().loading || get().loaded) return;
    set({ loading: true });
    try {
      const data = await api.get<Record<string, string>>('/landing-content');
      set({ content: { ...get().content, ...(data || {}) }, loaded: true, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  save: async (key, en, de) => {
    // Both languages travel together, as everywhere else on the site.
    const updated = await api.put<Record<string, string>>('/landing-content', {
      [key]: en,
      [`${key}_de`]: de,
    });
    set({ content: { ...get().content, ...(updated || {}) }, loaded: true });
  },
}));
